import { FC, useEffect } from 'react'

import { useCurrentModel } from '~/hooks'
import { useAvatar } from '~/hooks/useAvatar'
import { useMeasurements } from '~/hooks/useMeasurements'
import { usePartner } from '~/hooks/usePartner'
import { useTryon } from '~/hooks/useTryon'
import { TryonStorageInstance } from '~/hooks/useTryon/storage'

import { useActiveProductsContext } from '~/context/ActiveProducts'
import { useMeasurementsContext } from '~/context/Measurements'
import { useNavigation } from '~/context/Navigation'
import { useTryonContext } from '~/context/Tryon'

import { removeFromLocalStorage as clearInformDataSavedValues } from '~/screens/InformData/utils/formDataStorage'
import { shouldRemoveProductsFromTryOn } from '~/screens/Models/utils/shouldRemoveProductsFromTryOn'
import { IProcessingScreenProps } from '~/screens/ProcessingScreen/types'
import Tracking from '~/utils/tracking'

import { getSentences } from './constants'

export const withFileUploadAvatar = (ProcessingScreen: FC<IProcessingScreenProps>) => {
  const Component: React.FC = () => {
    const { createAvatarAsync, setAvatar } = useAvatar()
    const { navigate } = useNavigation()
    const { setCurrentModel, getCurrentModel } = useCurrentModel()
    const { resetMeasurements } = useMeasurements()
    const { clearStates: clearMeasurementsState } = useMeasurementsContext()
    const { clearStates, setTryonState, stateCurrentTryon } = useTryonContext()
    const { startTryon, removeTryon } = useTryon()
    const { getPartner } = usePartner()
    const { products } = useActiveProductsContext()

    const sentences = getSentences()

    const handleFailure = () => {
      navigate('ImageInputProcessingFailure')
    }

    const navigateToNextScreen = () => {
      const productsWithViton = products.some(p => p.has_viton)
      const productsWithOnlySizingTable = products.filter(p => p.has_size && !p.has_viton)

      if (productsWithViton || !productsWithOnlySizingTable.length) {
        navigate('Home')
        return
      }

      const currentModel = getCurrentModel()

      navigate('SizingSuggestionScreen', {
        identifiers: productsWithOnlySizingTable.map(p => p.identifier),
        avatarUUID: currentModel?.id,
      })
    }

    useEffect(() => {
      const createAvatar = async () => {
        try {
          const avatarBase64Image = localStorage.getItem('@doris_candidate_avatar')

          if (!avatarBase64Image) return

          const result = await createAvatarAsync({ image: avatarBase64Image })

          localStorage.removeItem('@doris_candidate_avatar')

          const { avatar_uuid, image_url, id, age, weight, height, age_group, gender } = result

          Tracking.logEvent('AVATAR_READY', {
            avatar: avatar_uuid,
            gender: gender,
            age_group: age_group,
            height: height,
            weight: weight,
            age: age,
            widget: true,
          })

          setAvatar({
            avatar_uuid: avatar_uuid,
            image_url: image_url,
            id: Number(id),
            age: age,
            height: height,
            weight: weight,
            gender: gender,
            age_group: age_group,
            model_to_tryon_id: Number(id),
          })

          setCurrentModel({
            id: avatar_uuid,
            skin: 1,
            stage_image: image_url,
            gender: gender,
            type: 'SELF_MODEL',
            age_group: age_group,
          })

          resetMeasurements({ data: ['stateCurrentMeasurements'], setState: clearMeasurementsState })

          const currentModel = getCurrentModel()

          const shouldRemoveProducts = shouldRemoveProductsFromTryOn({
            model: {
              id: avatar_uuid,
              skin: 1,
              stage_image: image_url,
              gender: gender,
              age_group: age_group,
              type: 'SELF_MODEL',
            },
            currentModel,
            products: stateCurrentTryon?.products,
          })

          if (shouldRemoveProducts) {
            removeTryon({ setState: clearStates })
            navigateToNextScreen()

            return
          }

          const currentStorage = TryonStorageInstance.get() || {}
          const { data: partner } = await getPartner()

          startTryon({
            data: {
              idModel: avatar_uuid,
              from: 'avatar',
              products: currentStorage,
              upscale: partner?.upscale,
            },
            setState: setTryonState,
          })

          clearInformDataSavedValues()

          navigateToNextScreen()
        } catch (error) {
          handleFailure()
        }
      }

      createAvatar()

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return <ProcessingScreen sentences={sentences} closeUrl="Home" />
  }

  Component.displayName = 'withFileUploadAvatar'

  return Component
}
